"use client";
import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Header from "../../../components/IntranetHeader";
import { supabase } from "../../../supabaseClient";

export default function Overview() {
  const [patients, setPatients] = useState([]);
  const [patientTab, setPatientTab] = useState("active");
  const [robloxFilter, setRobloxFilter] = useState("");
  const [createdSort, setCreatedSort] = useState("newest");
  const navigate = useNavigate();

  useEffect(() => {
    const fetchPatients = async () => {
      const { data, error } = await supabase
        .from("patients")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) {
        console.error("Error fetching patients:", error);
      } else {
        setPatients(data || []);
      }
    };

    fetchPatients();
  }, []);

  const triageColors = {
    A: "bg-red-600 text-white",
    B: "bg-orange-500 text-white",
    C: "bg-yellow-400 text-black",
    D: "bg-green-500 text-white",
    E: "bg-blue-500 text-white",
  };

  const HIGH_RISK_ALERTS = [
    "Medically Unstable",
    "Aggressive",
    "Requires Close Observation / Restraints",
  ];

  const flagRowColours = {
    low: "bg-green-100",
    medium: "bg-yellow-100",
    high: "bg-red-100",
  };

  const getRiskLevelFromAlerts = (alerts = []) => {
    const hasHigh = alerts.some((a) => HIGH_RISK_ALERTS.includes(a));

    if (hasHigh || alerts.length >= 3) return "high";
    if (alerts.length >= 2) return "medium";
    if (alerts.length === 1) return "low";

    return null;
  };

  const formatTime = (timeString) => {
    if (!timeString) return "";

    const date = new Date(timeString);

    return date.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const activePatients = patients.filter((p) => !p.archived);
  const archivedPatients = patients.filter((p) => p.archived);

  const visiblePatients = useMemo(() => {
    const source = patients.filter((patient) =>
      patientTab === "archived" ? Boolean(patient.archived) : !patient.archived
    );
    const needle = robloxFilter.trim().toLowerCase();

    return source
      .filter((patient) => {
        if (!needle) return true;
        return String(patient.roblox_username || "").toLowerCase().includes(needle);
      })
      .slice()
      .sort((a, b) => {
        const aTime = a.created_at ? new Date(a.created_at).getTime() : 0;
        const bTime = b.created_at ? new Date(b.created_at).getTime() : 0;
        return createdSort === "oldest" ? aTime - bTime : bTime - aTime;
      });
  }, [createdSort, patientTab, patients, robloxFilter]);

  const formatCreatedAt = (value) => {
    if (!value) return "Not recorded";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "Not recorded";
    return date.toLocaleString([], {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getActiveRowClass = (patient) => {
    const level = getRiskLevelFromAlerts(patient.alerts);
    if (!level) return "border-b border-black";
    return `border-b border-black ${flagRowColours[level]} animate-pulse`;
  };

  const getArchivedRowClass = (patient) => {
    const level = getRiskLevelFromAlerts(patient.alerts);
    if (!level) return "border-b border-black opacity-70";
    return `border-b border-black opacity-70 ${flagRowColours[level]}`;
  };

  return (
    <div className="flex flex-col min-h-screen">
      <Header />

      <div className="w-full px-[5px] mt-5">
        <div className="flex items-center justify-between mb-6">
          <h1 className="nhsuk-heading-l mb-0">
            Electronic Clinical &amp; Management Information System
          </h1>

          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => navigate("/intranet/ecamis/check-in-queue")}
              className="nhsuk-button nhsuk-button--secondary"
              type="button"
            >
              Check-in queue
            </button>
            <button
              onClick={() => navigate("/intranet/ecamis/clinical-worklist")}
              className="nhsuk-button nhsuk-button--secondary"
              type="button"
            >
              Clinical worklist
            </button>
            <button
              onClick={() => navigate("/intranet/wards")}
              className="nhsuk-button"
              type="button"
            >
              Wards & beds
            </button>
          </div>
        </div>

        <div className="nhsuk-tabs" style={{ marginTop: "24px" }}>
          <ul className="nhsuk-tabs__list" role="tablist" aria-label="Patient records">
            <li className={`nhsuk-tabs__list-item${patientTab === "active" ? " nhsuk-tabs__list-item--selected" : ""}`}>
              <button
                type="button"
                className="nhsuk-tabs__tab"
                onClick={() => setPatientTab("active")}
                aria-selected={patientTab === "active"}
              >
                Active patients ({activePatients.length})
              </button>
            </li>
            <li className={`nhsuk-tabs__list-item${patientTab === "archived" ? " nhsuk-tabs__list-item--selected" : ""}`}>
              <button
                type="button"
                className="nhsuk-tabs__tab"
                onClick={() => setPatientTab("archived")}
                aria-selected={patientTab === "archived"}
              >
                Archived patients ({archivedPatients.length})
              </button>
            </li>
          </ul>
        </div>

        <section className="nhsuk-card" style={{ marginBottom: "24px" }}>
          <div className="nhsuk-card__content" style={{ paddingBottom: "20px" }}>
            <div style={{ display: "grid", gridTemplateColumns: "minmax(240px, 1fr) minmax(220px, 300px)", gap: "16px", alignItems: "end" }}>
              <div className="nhsuk-form-group" style={{ marginBottom: 0 }}>
                <label className="nhsuk-label" htmlFor="ecamis-roblox-filter">
                  Filter by Roblox username
                </label>
                <input
                  id="ecamis-roblox-filter"
                  className="nhsuk-input"
                  type="search"
                  value={robloxFilter}
                  onChange={(event) => setRobloxFilter(event.target.value)}
                  placeholder="e.g. Builderman"
                  autoComplete="off"
                />
              </div>

              <div className="nhsuk-form-group" style={{ marginBottom: 0 }}>
                <label className="nhsuk-label" htmlFor="ecamis-created-sort">
                  Date added
                </label>
                <select
                  id="ecamis-created-sort"
                  className="nhsuk-select"
                  value={createdSort}
                  onChange={(event) => setCreatedSort(event.target.value)}
                >
                  <option value="newest">Newest to oldest</option>
                  <option value="oldest">Oldest to newest</option>
                </select>
              </div>
            </div>
          </div>
        </section>

        <div className="flex items-center justify-between mb-4">
          <h2 className="nhsuk-heading-m mb-0">
            {patientTab === "archived" ? "Archived patients" : "Active patients"}
          </h2>
          <span className="nhsuk-body-s mb-0">
            Showing {visiblePatients.length} of {patientTab === "archived" ? archivedPatients.length : activePatients.length}
          </span>
        </div>

        <div className="overflow-x-auto w-full">
          <table className="nhsuk-table w-full border-collapse">
            <thead>
              <tr className="border-b-[3px] border-black">
                <th className="pb-2 text-left">Name</th>
                <th className="pb-2 text-center">Triage</th>
                <th className="pb-2 text-left">Presenting Complaint</th>
                <th className="pb-2 text-left">Age</th>
                <th className="pb-2 text-left">Arrival</th>
                <th className="pb-2 text-left">Date added</th>
                <th className="pb-2 text-left">Location</th>
                <th className="pb-2 text-left">Bed</th>
                <th className="pb-2 text-left">Actions</th>
              </tr>
            </thead>
            <tbody>
              {visiblePatients.map((patient) => (
                <tr
                  key={patient.id}
                  className={patientTab === "archived" ? getArchivedRowClass(patient) : getActiveRowClass(patient)}
                >
                  <td style={{ whiteSpace: "normal" }}>
                    <div className="font-semibold">{patient.full_name}</div>
                    {patient.roblox_username && (
                      <div className="text-sm text-gray-600">
                        @{patient.roblox_username}
                      </div>
                    )}
                  </td>

                  <td className="text-center">
                    {patient.triage_band && (
                      <span
                        className={`inline-block ${
                          triageColors[patient.triage_band]
                        } text-xs px-1.5 py-0.5 rounded-full font-bold`}
                      >
                        {patient.triage_band}
                      </span>
                    )}
                  </td>

                  <td>{patient.presenting_complaint}</td>
                  <td>{patient.age}</td>
                  <td>{formatTime(patient.arrival_time)}</td>
                  <td>{formatCreatedAt(patient.created_at)}</td>
                  <td>{patient.location}</td>
                  <td>{patient.bed || "N/A"}</td>

                  <td>
                    <Link
                      to={`/intranet/ecamis/patient/${patient.id}`}
                      className="text-blue-600 underline"
                    >
                      Open record
                    </Link>
                  </td>
                </tr>
              ))}

              {visiblePatients.length === 0 && (
                <tr>
                  <td colSpan={9} className="py-4 text-center">
                    {robloxFilter.trim()
                      ? `No ${patientTab === "archived" ? "archived" : "active"} patients match that Roblox username.`
                      : `No ${patientTab === "archived" ? "archived" : "active"} patients`}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
